import React, { FunctionComponent, useEffect, useLayoutEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { Button, Card, Icon } from 'rs-emd-ui-atoms';
import { getLabel } from '../../../..';
import { ILabel } from '../../../../models/common/label.model';
import { MediaType } from '../../../../models/pdp/pdp-media-item.model';
import { getThumbnail, IPDPMedia, isPDPMediaItem } from '../../../../models/pdp/pdp-media.model';
import pcb3d from './../../../../images/pcb-3d.svg';
import snapeda from './../../../../images/snapeda.svg';
import styles from './media-modal-component.module.scss';

type MediaModalProps = {
  data: IPDPMedia;
  labels?: ILabel[] | null;
  currentMediaBrand?: string;
  currentMediaMPN?: string;
  selectedMediaIndex: number;
  isPCBAvailable?: boolean;
  onCloseCallback: () => void;
};

export const MediaModal: FunctionComponent<MediaModalProps> = (props: MediaModalProps) => {
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(props.selectedMediaIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showPCBBtn, setShowPCBBtn] = useState(!isPDPMediaItem(props.data.media[selectedMediaIndex]));

  useLayoutEffect(() => {
    document.body.classList.add('no-scroll');

    return () => {
      // Cleanup & unsubscribe
      document.body.classList.remove('no-scroll');
    };
  }, []);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (selectedMediaIndex < props.data.media?.length - 1) {
        setSelectedMediaIndex(selectedMediaIndex + 1);
        setShowPCBBtn(!isPDPMediaItem(props.data.media[selectedMediaIndex + 1]));
      }
    },
    onSwipedRight: () => {
      if (selectedMediaIndex !== 0) {
        setSelectedMediaIndex(selectedMediaIndex - 1);
        setShowPCBBtn(!isPDPMediaItem(props.data.media[selectedMediaIndex - 1]));
      }
    },
    trackMouse: true,
    trackTouch: true,
  });

  // add pcb script
  useEffect(() => {
    if (
      props.currentMediaBrand &&
      props.currentMediaMPN &&
      showPCBBtn &&
      document.getElementById('snapeda-id') === null &&
      document.getElementById('pcb-button-mobile') !== null
    ) {
      const script = document.createElement('script');

      script.src = 'https://www.snapeda.com/distributor/files/plugins/snapeda/snapeda.min.js?token=2ccb86c928f0ae7f6cd304414a7686';
      script.setAttribute('id', 'snapeda-id');
      script.setAttribute('defer', 'true');
      script.setAttribute('autoload', 'true');
      script.setAttribute('part', `${encodeURIComponent(props.currentMediaMPN)}`);
      script.setAttribute('manufacturer', `${encodeURIComponent(props.currentMediaBrand)}`);
      script.setAttribute('token', '2ccb86c928f0ae7f6cd304414a7686');
      script.setAttribute('container', '#pcb-button-mobile');
      script.setAttribute('host', 'https://www.snapeda.com');
      script.setAttribute('lang', 'en');

      document.body.appendChild(script);
    }
  }, [props.currentMediaBrand, props.currentMediaMPN, showPCBBtn]);

  // remove pcb script
  useEffect(() => {
    if (!showPCBBtn) removePCBScript();
  }, [showPCBBtn]);

  return (
    <div className={styles['gallery-modal-container']}>
      <Card className={styles['header']} shadow='lg' squareCorners={true}>
        <span className={styles.title}>{getLabel(props.labels, 'gallery_title')}</span>
        <Icon
          name='close'
          onClick={() => {
            // remove pcb script
            if (showPCBBtn) removePCBScript();

            props.onCloseCallback();
          }}
        />
      </Card>

      <div className={styles.content}>
        {/* Main container */}
        <div className={styles['main-container']}>
          {/* Left chevron (tablet only) */}
          <Icon
            name='chev-left'
            className={`
                ${styles.chevron}
                ${styles.left}
                ${selectedMediaIndex === 0 ? styles.disabled : ''}`}
            onClick={() => {
              setSelectedMediaIndex(selectedMediaIndex - 1);
              setShowPCBBtn(!isPDPMediaItem(props.data.media[selectedMediaIndex - 1]));
            }}></Icon>

          <div className={`${styles.media}`} {...handlers}>
            {renderMedia()}
          </div>

          {/* Right chevron (tablet only) */}
          <Icon
            name='chev-right'
            className={`
                ${styles.chevron}
                ${styles.right}
                ${selectedMediaIndex === props.data.media.length - 1 ? styles.disabled : ''}`}
            onClick={() => {
              setSelectedMediaIndex(selectedMediaIndex + 1);
              setShowPCBBtn(!isPDPMediaItem(props.data.media[selectedMediaIndex + 1]));
            }}></Icon>
        </div>

        {/* Thumbnails */}
        <div className={styles.thumbnails}>
          {props.data &&
            props.data.media.map((m, index) => {
              if (isPDPMediaItem(m)) {
                return (
                  <div className={styles['thumbnail-wrapper']}>
                    <div
                      key={`m-thumbnail-${m.id}`}
                      className={`${styles.thumbnail} ${selectedMediaIndex === index ? styles.selected : ''}`}
                      onClick={() => {
                        setSelectedMediaIndex(index);
                        setShowPCBBtn(false);
                      }}>
                      {m.mediaType !== MediaType.Image && (
                        <div className={styles.badge}>
                          <Icon
                            name={m.mediaType === MediaType.YoutubeVideo ? 'play2' : m.mediaType === MediaType.View_3D ? '3d' : 'file'}
                            width={16}
                            height={16}></Icon>
                        </div>
                      )}
                      <img id={m.id} src={getThumbnail(m, props.data)} alt={m.name} />
                    </div>
                  </div>
                );
              } else
                return (
                  <div className={styles['thumbnail-wrapper']}>
                    <div
                      key='m-thumbnail-pcb'
                      className={`${styles.thumbnail} ${selectedMediaIndex === index ? styles.selected : ''}`}
                      onClick={() => {
                        setSelectedMediaIndex(index);
                        setShowPCBBtn(true);
                      }}>
                      <img src={pcb3d} alt={'thumbnail-pcb'} className={styles.pcb} />
                    </div>
                  </div>
                );
            })}
        </div>
      </div>
    </div>
  );

  function removePCBScript() {
    if (document.getElementById('snapeda-id') !== null) {
      const snapedaScript = document.getElementById('snapeda-id');

      if (snapedaScript) document.body.removeChild(snapedaScript);
    }
  }

  function renderMedia() {
    const media = props.data.media[selectedMediaIndex];

    if (isPDPMediaItem(media)) {
      // IMAGE?
      if (media.mediaType === MediaType.Image)
        return (
          // IMAGE with pinch-to-zoom and pan functionality
          <TransformWrapper
            panning={{ disabled: !isZoomed }}
            initialScale={1}
            initialPositionX={0}
            initialPositionY={0}
            onZoomStop={(s: any) => {
              setIsZoomed(s.scale > 1);
            }}>
            <TransformComponent>
              <img
                src={props.data.mediaUrl.replace(/\[width\]/g, '600').replace('[name]', media.name)}
                alt={media.name}
                className={styles.image}></img>
            </TransformComponent>
          </TransformWrapper>
        );
      // VIDEO?
      else if (media.mediaType === MediaType.YoutubeVideo)
        return (
          <iframe className={styles.video} title={media.id} src={media.url} allow='autoplay; encrypted-media' frameBorder='0'></iframe>
        );
      // 3D IMAGE?
      else if (media.mediaType === MediaType.View_3D) {
        return (
          <iframe
            className={styles['three-d']}
            title={media.id}
            src={media.url}
            allow='autoplay; encrypted-media'
            frameBorder='0'
            scrolling='no'></iframe>
        );
      }
    } // PCB?
    else if (props.isPCBAvailable) {
      return (
        <div className={styles.pcb}>
          <img src={pcb3d} alt='pcb-3d' className={styles.logo}></img>

          {showPCBBtn && (
            <>
              <Button
                className={styles['pcb-button-overlay']}
                text={'View PCB Symbol, Footprint & 3D Model'}
                buttonType='text'
                onClick={(e: any) => {
                  const snapeda = document.getElementById('btn-popup-modal'); //snapeda button tag id
                  if (snapeda !== null) snapeda.click();
                }}
              />
              {/*restrict size to pcb-button-overlay's size*/}
              <div className={styles['pcb-button-wrapper']}>
                <div id='pcb-button-mobile' />
              </div>
            </>
          )}

          <img src={snapeda} alt='snapeda-logo' className={styles.logo}></img>
        </div>
      );
    }
  }
};

export default MediaModal;
